'use client';

import React, { useState } from 'react';
import {
    Type, Image as ImageIcon, MousePointer2,
    Square, Circle, Download, Plus, Trash2,
    Bold, Italic, Underline, AlignLeft, AlignCenter, AlignRight, AlignJustify,
    List, ListOrdered, Layout as LayoutIcon, Columns, FileText, Highlighter, Eraser,
    History, Share2, Play, Sparkles, RefreshCw, Check, ChevronUp, ChevronDown,
    Strikethrough, Type as TypeIcon,
    FilePen,
    LetterText,
    LetterTextIcon,
    LucideALargeSmall,
    AirVent,
    Airplay,
    FactoryIcon,
    ListRestart,
    RotateCw,
    Expand,
    TextSelect,
    TextSelection,
    TextQuote,
    TextCursor,
    CaseSensitive,
    WrapText,
    ArrowDownWideNarrow,
    RefreshCcwDot
} from 'lucide-react';
import { cn } from '../lib/utils';
import { SlideElement, PresentationSource } from '../lib/types';

interface ToolbarProps {
    onAddText: () => void;
    onAddImage: (file: File) => void;
    onAddShape: (type: any) => void;
    onAddSlide: () => void;
    onExport: () => void;
    onFormatText: (updates: any) => void;
    onDeleteElement: () => void;
    onGenerateImageClick: () => void;
    onGenerateInfographicClick: () => void;
    onUploadImageClick: () => void;
    onApplyLayout: (type: 'title' | 'content' | 'split') => void;
    onPlay: () => void;
    selectedElement: SlideElement | null;
    appName: string;
    onAiAction: (id: string, action: 'shorten' | 'reframe' | 'lengthen') => void;
    onAiResponseAction: (action: 'replace' | 'addBelow' | 'regenerate') => void;
    aiResponse: string | null;
    isAiLoading: boolean;
    refineResponse?: string | null;
    onRefineAction: (actionId: string, actionType: 'shorten' | 'reframe' | 'lengthen') => void;
    onNewPresentation: () => void;
    onLoadPresentation: (input: File | string) => void;
    appBgColor?: string;
    uiScale: number;
    source: PresentationSource | null;
}

const FONTS = [
    'Inter', 'Lato', 'Open Sans', 'Poppins', 'Roboto', 'Roboto Slab',
    'Georgia', 'Playfair Display', 'Merriweather', 'Nunito Sans',
    'Montserrat', 'Oswald', 'Raleway', 'Ubuntu', 'Lora',
    'PT Sans', 'PT Serif', 'Play', 'Arvo', 'Kanit', 'Times New Roman', 'Arial'
];
const FONT_SIZES = [12, 14, 16, 18, 20, 24, 28, 32, 36, 48, 64, 72, 96];

const SHAPE_CATEGORIES = [
    {
        name: 'Basic Shapes',
        shapes: ['rect', 'circle', 'triangle', 'rightTriangle', 'rhombus', 'parallelogram', 'trapezoid', 'pentagon', 'hexagon', 'heptagon', 'octagon', 'heart', 'smiley', 'sun', 'moon']
    },
    {
        name: 'Arrows',
        shapes: ['arrowRight', 'arrowLeft', 'arrowUp', 'arrowDown', 'arrowLeftRight', 'arrowUpDown']
    },
    {
        name: 'Stars & Symbols',
        shapes: ['star4', 'star5', 'star6', 'star8', 'cloud', 'lightning']
    },
    {
        name: 'Equation Shapes',
        shapes: ['plus', 'minus', 'multiply', 'divide', 'equal']
    }
];

const ShapeIcon = ({ type, className }: { type: string, className?: string }) => {
    switch (type) {
        case 'rect': return <svg viewBox="0 0 24 24" className={className}><rect x="4" y="4" width="16" height="16" rx="2" fill="currentColor" /></svg>;
        case 'circle': return <svg viewBox="0 0 24 24" className={className}><circle cx="12" cy="12" r="9" fill="currentColor" /></svg>;
        case 'triangle': return <svg viewBox="0 0 24 24" className={className}><path d="M12 4L20 18H4L12 4Z" fill="currentColor" /></svg>;
        case 'rightTriangle': return <svg viewBox="0 0 24 24" className={className}><path d="M4 4V20H20L4 4Z" fill="currentColor" /></svg>;
        case 'rhombus': return <svg viewBox="0 0 24 24" className={className}><path d="M12 4L20 12L12 20L4 12L12 4Z" fill="currentColor" /></svg>;
        case 'parallelogram': return <svg viewBox="0 0 24 24" className={className}><path d="M7 4H21L17 20H3L7 4Z" fill="currentColor" /></svg>;
        case 'trapezoid': return <svg viewBox="0 0 24 24" className={className}><path d="M6 4H18L21 20H3L6 4Z" fill="currentColor" /></svg>;
        case 'pentagon': return <svg viewBox="0 0 24 24" className={className}><path d="M12 3L21 9V19H3V9L12 3Z" fill="currentColor" /></svg>;
        case 'hexagon': return <svg viewBox="0 0 24 24" className={className}><path d="M12 3L20 7.5V16.5L12 21L4 16.5V7.5L12 3Z" fill="currentColor" /></svg>;
        case 'heptagon': return <svg viewBox="0 0 24 24" className={className}><path d="M12 3L18.5 6L21 13L16 20H8L3 13L5.5 6L12 3Z" fill="currentColor" /></svg>;
        case 'octagon': return <svg viewBox="0 0 24 24" className={className}><path d="M8.5 3H15.5L21 8.5V15.5L15.5 21H8.5L3 15.5V8.5L8.5 3Z" fill="currentColor" /></svg>;
        case 'heart': return <svg viewBox="0 0 24 24" className={className}><path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" fill="currentColor" /></svg>;
        case 'smiley': return <svg viewBox="0 0 24 24" className={className}><circle cx="12" cy="12" r="9" fill="currentColor" fillOpacity="0.2" stroke="currentColor" strokeWidth="2" /><circle cx="9" cy="9" r="1.5" fill="currentColor" /><circle cx="15" cy="9" r="1.5" fill="currentColor" /><path d="M8 15C8 15 9.5 17 12 17C14.5 17 16 15 16 15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>;
        case 'sun': return <svg viewBox="0 0 24 24" className={className}><circle cx="12" cy="12" r="5" fill="currentColor" /><path d="M12 2V4M12 20V22M4 12H2M22 12H20M19.07 4.93L17.66 6.34M6.34 17.66L4.93 19.07M4.93 4.93L6.34 6.34M17.66 17.66L19.07 19.07" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>;
        case 'moon': return <svg viewBox="0 0 24 24" className={className}><path d="M12 3a9 9 0 1 0 9 9 7 7 0 0 1-9-9" fill="currentColor" /></svg>;
        case 'arrowRight': return <svg viewBox="0 0 24 24" className={className}><path d="M4 11h11.5l-3.5-3.5 1.5-1.5 6 6-6 6-1.5-1.5 3.5-3.5H4z" fill="currentColor" /></svg>;
        case 'arrowLeft': return <svg viewBox="0 0 24 24" className={className}><path d="M20 11H8.5l3.5-3.5L10.5 6l-6 6 6 6 1.5-1.5-3.5-3.5H20z" fill="currentColor" /></svg>;
        case 'arrowUp': return <svg viewBox="0 0 24 24" className={className}><path d="M13 20V8.5l3.5 3.5 1.5-1.5-6-6-6 6 1.5 1.5 3.5-3.5V20z" fill="currentColor" /></svg>;
        case 'arrowDown': return <svg viewBox="0 0 24 24" className={className}><path d="M11 4v11.5l-3.5-3.5-1.5 1.5 6 6 6-6-1.5-1.5-3.5 3.5V4z" fill="currentColor" /></svg>;
        case 'arrowLeftRight': return <svg viewBox="0 0 24 24" className={className}><path d="M4 12l4-4v3h8V8l4 4-4 4v-3H8v3l-4-4z" fill="currentColor" /></svg>;
        case 'arrowUpDown': return <svg viewBox="0 0 24 24" className={className}><path d="M12 4l-4 4h3v8H8l4 4 4-4h-3V8h3l-4-4z" fill="currentColor" /></svg>;
        case 'star4': return <svg viewBox="0 0 24 24" className={className}><path d="M12 2l2 8 8 2-8 2-2 8-2-8-8-2 8-2 2-8z" fill="currentColor" /></svg>;
        case 'star5': return <svg viewBox="0 0 24 24" className={className}><path d="M12 2l2.5 7.5H22l-6 4.5 2.5 7.5-6.5-4.5-6.5 4.5 2.5-7.5-6-4.5h7.5L12 2z" fill="currentColor" /></svg>;
        case 'star6': return <svg viewBox="0 0 24 24" className={className}><path d="M12 2l3 5 6-1-4 4.5 2 6.5-7-3-7 3 2-6.5-4-4.5 6 1 3-5z" fill="currentColor" /></svg>;
        case 'star8': return <svg viewBox="0 0 24 24" className={className}><path d="M12 2l2 4 4-2-2 4 4 2-4 2 2 4-4-2-2 4-2-4-4 2 2-4-4-2 4-2-2-4 4 2 2-4z" fill="currentColor" /></svg>;
        case 'cloud': return <svg viewBox="0 0 24 24" className={className}><path d="M17.5 19c-3 0-5.5-2.5-5.5-5.5 0-.2 0-.4.1-.6-2-.8-3.6-2.5-4.1-4.7C6.5 7.4 5 6 3 6c-1.7 0-3 1.3-3 3s1.3 3 3 3c.4 0 .8-.1 1.2-.2C4.1 13 5 14.5 6.5 15.5c.3 1.8 1.4 3.4 3 4.3.4.2.8.3 1.2.4 1.2.4 2.5.6 3.8.6 4.4 0 8-3.6 8-8s-3.6-8-8-8c-.4 0-.8 0-1.2.1-1.2.2-2.3.7-3.2 1.5C11 4.2 12.4 3.5 14 3.5c4.1 0 7.5 3.4 7.5 7.5s-1.8 7.5-4 8z" fill="currentColor" transform="scale(0.8) translate(3,3)" /></svg>;
        case 'lightning': return <svg viewBox="0 0 24 24" className={className}><path d="M13 2L3 14h9v8l10-12h-9l3-8z" fill="currentColor" /></svg>;
        case 'plus': return <svg viewBox="0 0 24 24" className={className}><path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" fill="currentColor" /></svg>;
        case 'minus': return <svg viewBox="0 0 24 24" className={className}><path d="M19 13H5v-2h14v2z" fill="currentColor" /></svg>;
        case 'multiply': return <svg viewBox="0 0 24 24" className={className}><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12 19 6.41z" fill="currentColor" /></svg>;
        case 'divide': return <svg viewBox="0 0 24 24" className={className}><circle cx="12" cy="7" r="2" fill="currentColor" /><circle cx="12" cy="17" r="2" fill="currentColor" /><rect x="5" y="11" width="14" height="2" fill="currentColor" /></svg>;
        case 'equal': return <svg viewBox="0 0 24 24" className={className}><path d="M19 10H5V8h14v2zm0 6H5v-2h14v2z" fill="currentColor" /></svg>;
        default: return <div className="w-6 h-6 bg-slate-200 rounded-sm" />;
    }
};

const ShapesDropdown = ({ onAddShape, uiScale }: { onAddShape: (type: string) => void, uiScale: number }) => {
    const [isOpen, setIsOpen] = useState(false);

    // Wire up the toggle to the window so the parent button can trigger it
    React.useEffect(() => {
        (window as any)._toggleShapesMenu = () => setIsOpen(!isOpen);
        return () => { (window as any)._toggleShapesMenu = undefined; };
    }, [isOpen]);

    if (!isOpen) return null;

    return (
        <div
            className="absolute top-full left-0 mt-2 bg-white border border-slate-200 rounded-2xl shadow-2xl p-4 overflow-y-auto z-[100] animate-in fade-in zoom-in-95 duration-200"
            style={{
                width: `${320 * uiScale}px`,
                maxHeight: `${480 * uiScale}px`
            }}
        >
            <div className="flex flex-col gap-6">
                {SHAPE_CATEGORIES.map(cat => (
                    <div key={cat.name} className="flex flex-col gap-2">
                        <span className="text-[10px] font-black uppercase tracking-widest text-slate-400 px-1" style={{ fontSize: `${10 * uiScale}px` }}>{cat.name}</span>
                        <div className="grid grid-cols-5 gap-1">
                            {cat.shapes.map(s => (
                                <button
                                    key={s}
                                    onClick={() => { onAddShape(s); setIsOpen(false); }}
                                    className="aspect-square hover:bg-slate-50 rounded-lg flex items-center justify-center border border-transparent hover:border-slate-100 transition-all group p-1"
                                    title={s}
                                >
                                    <div className="w-full h-full bg-slate-100 rounded-sm group-hover:bg-blue-100 group-hover:scale-110 transition-all flex items-center justify-center overflow-hidden p-1.5">
                                        <ShapeIcon type={s} className="w-full h-full text-slate-400 group-hover:text-blue-600 transition-colors" />
                                    </div>
                                </button>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export const Toolbar: React.FC<ToolbarProps> = ({
    onAddText, onAddImage, onAddShape, onAddSlide, onExport,
    onFormatText, onDeleteElement, onApplyLayout, onPlay, selectedElement,
    onNewPresentation, onLoadPresentation, appBgColor = '#B7472A', uiScale,
    source, onGenerateImageClick, onGenerateInfographicClick, onUploadImageClick,
    onRefineAction, refineResponse, isAiLoading
}) => {
    const fileInputRef = React.useRef<HTMLInputElement>(null);
    const [activeTab, setActiveTab] = useState('Home');
    const [showLayouts, setShowLayouts] = useState(false);
    const [showAiMenu, setShowAiMenu] = useState(false);  // For the new Refine dropdown
    const [showFileMenu, setShowFileMenu] = useState(false);
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [uploadType, setUploadType] = useState<'file' | 'link'>('file');
    const [uploadUrl, setUploadUrl] = useState('');
    const [showChangeCaseMenu, setShowChangeCaseMenu] = useState(false);
    const [showCharSpacingMenu, setShowCharSpacingMenu] = useState(false);
    const [showBulletMenu, setShowBulletMenu] = useState(false);
    const [showNumberingMenu, setShowNumberingMenu] = useState(false);

    // Change case helper functions
    const applyChangeCase = (caseType: string) => {
        if (!isText || !selectedElement) return;
        const currentText = (selectedElement as any).content || '';
        let newText = currentText;
        switch (caseType) {
            case 'sentence':
                newText = currentText.charAt(0).toUpperCase() + currentText.slice(1).toLowerCase();
                break;
            case 'lowercase':
                newText = currentText.toLowerCase();
                break;
            case 'uppercase':
                newText = currentText.toUpperCase();
                break;
            case 'capitalize':
                newText = currentText.split(' ').map((word: string) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ');
                break;
            case 'toggle':
                newText = currentText.split('').map((char: string) => char === char.toUpperCase() ? char.toLowerCase() : char.toUpperCase()).join('');
                break;
        }
        onFormatText({ content: newText });
        setShowChangeCaseMenu(false);
    };

    const isText = selectedElement?.type === 'text';
    const isShape = selectedElement?.type === 'shape';
    const isImage = selectedElement?.type === 'image';

    const PPT_RED = '#B7472A';

    return (
        <div
            className="flex flex-col border-b border-slate-200 bg-white z-50 select-none"
            style={{ fontSize: `${12 * uiScale}px` }}
        >
            {/* Main Toolbar Row */}
            <div
                className="bg-white flex items-center justify-center border-b border-slate-200/60 transition-all duration-300 relative z-[1000]"
                style={{ minHeight: `${120 * uiScale}px`, overflow: 'visible' }}
            >
                {/* Slides Group */}
                <div className={cn("flex flex-col items-center border-r border-slate-100 px-3 gap-1 h-full py-2", uiScale < 0.84 ? "min-w-0" : "min-w-fit")}>
                    <div className="flex items-center justify-center gap-1 my-auto">
                        {/*<button
                            onClick={onAddSlide}
                            className="flex flex-col items-right gap-1.5 px-3 py-1.5 hover:bg-slate-50 rounded-lg group transition-all min-w-[45px]"
                        >
                            <div className="bg-orange-50 p-1.5 rounded-lg group-hover:scale-110 transition-transform">
                                <Plus size={Math.max(14, 20 * uiScale)} style={{ color: appBgColor }} />
                            </div>
                            <span className="text-[10px] font-bold text-slate-700">New Slide</span>
                        </button>

                        <div className="w-[1px] h-10 bg-slate-100 mx-1" /> */}

                        <div className="relative">
                            <button
                                onClick={() => setShowLayouts(!showLayouts)}
                                className={cn("flex flex-col items-left gap-1.5 px-3 py-1.5 hover:bg-slate-50 rounded-lg transition-all min-w-[45px]", showLayouts && "bg-slate-100")}
                            >
                                <div className="bg-orange-50 p-1.5 rounded-lg group-hover:scale-110 transition-transform">
                                    {/* <LayoutIcon size={Math.max(14, 20 * uiScale)} className="text-slate-500" /> */}
                                    <Plus size={Math.max(14, 20 * uiScale)} style={{ color: appBgColor }} />
                                </div>
                                <span className="text-[10px] font-medium text-slate-500 whitespace-nowrap">Slides</span>
                            </button>
                            {showLayouts && (
                                <div className="absolute top-full left-0 mt-2 bg-white border border-slate-200 rounded-2xl shadow-3xl p-2 w-60 flex flex-col gap-1 z-[100] animate-in fade-in zoom-in-95 duration-200 ring-4 ring-black/5">
                                    <button
                                        onClick={onAddSlide}
                                        className="flex items-center gap-3 px-4 py-3 hover:bg-slate-50 rounded-xl text-[13px] font-bold text-slate-700 transition-all border border-transparent hover:border-slate-100 group"
                                    >
                                        <div className="p-2 bg-blue-50 text-blue-600 rounded-lg group-hover:bg-blue-600 group-hover:text-white transition-colors">
                                            <FilePen size={18} />
                                        </div>
                                        Blank Slide
                                    </button>
                                    <button
                                        onClick={() => { onApplyLayout('title'); setShowLayouts(false); }}
                                        className="flex items-center gap-3 px-4 py-3 hover:bg-slate-50 rounded-xl text-[13px] font-bold text-slate-700 transition-all border border-transparent hover:border-slate-100 group"
                                    >
                                        <div className="p-2 bg-blue-50 text-blue-600 rounded-lg group-hover:bg-blue-600 group-hover:text-white transition-colors">
                                            <FileText size={18} />
                                        </div>
                                        Title Slide
                                    </button>
                                    <button
                                        onClick={() => { onApplyLayout('content'); setShowLayouts(false); }}
                                        className="flex items-center gap-3 px-4 py-3 hover:bg-slate-50 rounded-xl text-[13px] font-bold text-slate-700 transition-all border border-transparent hover:border-slate-100 group"
                                    >
                                        <div className="p-2 bg-emerald-50 text-emerald-600 rounded-lg group-hover:bg-emerald-600 group-hover:text-white transition-colors">
                                            <List size={18} />
                                        </div>
                                        Title & Content
                                    </button>
                                    <button
                                        onClick={() => { onApplyLayout('split'); setShowLayouts(false); }}
                                        className="flex items-center gap-3 px-4 py-3 hover:bg-slate-50 rounded-xl text-[13px] font-bold text-slate-700 transition-all border border-transparent hover:border-slate-100 group"
                                    >
                                        <div className="p-2 bg-purple-50 text-purple-600 rounded-lg group-hover:bg-purple-600 group-hover:text-white transition-colors">
                                            <Columns size={18} />
                                        </div>
                                        Two Columns
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                    {uiScale >= 0.45 && <span className="text-[9px] uppercase tracking-[0.2em] font-black text-slate-300 mt-auto">Slides</span>}
                </div>


                {/* Font Group */}
                <div className={cn("flex items-center flex-col border-r border-slate-200 h-full py-2 relative justify-center gap-2", uiScale < 0.85 ? "px-1" : "px-4")}>
                    {/* Top Row: Font Family, Size, A^, Av, Clear */}
                    <div className="flex items-center gap-3">
                        {/* Font Family Dropdown */}
                        <div className="flex items-center bg-white border border-slate-300 rounded-[3px] h-[28px] overflow-hidden">
                            <select
                                disabled={!isText}
                                value={(selectedElement as any)?.fontFamily || 'Open Sans'}
                                onChange={(e) => onFormatText({ fontFamily: e.target.value })}
                                className={cn("h-full px-1.5 text-slate-800 font-normal bg-transparent outline-none disabled:opacity-50 appearance-none cursor-pointer", uiScale < 0.85 ? "w-20" : "w-32")}
                                style={{ fontSize: `${Math.max(10, 12 * uiScale)}px` }}
                            >
                                {FONTS.map(f => <option key={f} value={f}>{f}</option>)}
                            </select>
                            <ChevronDown size={13} className="text-slate-400 mr-1" />
                        </div>

                        {/* Font Size Dropdown */}
                        <div className="flex items-center bg-white border border-slate-300 rounded-[3px] h-[28px] overflow-hidden w-15">
                            <select
                                disabled={!isText}
                                value={(selectedElement as any)?.fontSize || 10}
                                onChange={(e) => onFormatText({ fontSize: parseInt(e.target.value) })}
                                className="h-full w-full px-1 text-slate-800 font-normal bg-transparent outline-none disabled:opacity-50 appearance-none cursor-pointer text-center"
                                style={{ fontSize: `${Math.max(10, 12 * uiScale)}px` }}
                            >
                                {FONT_SIZES.map(s => <option key={s} value={s}>{s}</option>)}
                            </select>
                            <ChevronDown size={13} className="text-slate-400 mr-0.5" />
                        </div>

                        {/* Increase Font Size (A^) */}
                        <button
                            disabled={!isText}
                            onClick={() => onFormatText({ fontSize: Math.min(200, ((selectedElement as any)?.fontSize || 24) + 2) })}
                            className="w-9 h-[28px] flex items-center justify-center hover:bg-slate-100 rounded-[3px] transition-colors disabled:opacity-40"
                            title="Increase Font Size"
                        >
                            <span className="text-[16px] font-medium text-slate-600">A</span><ChevronUp size={10} color={'black'} />
                        </button>

                        {/* Decrease Font Size (Av) */}
                        <button
                            disabled={!isText}
                            onClick={() => onFormatText({ fontSize: Math.max(8, ((selectedElement as any)?.fontSize || 24) - 2) })}
                            className="w-9 h-[28px] flex items-center justify-center hover:bg-slate-100 rounded-[3px] transition-colors disabled:opacity-40"
                            title="Decrease Font Size"
                        >
                            <span className="text-[14px] font-medium text-slate-600">A</span><ChevronDown size={10} color={'black'} />
                        </button>

                        {/* Character Spacing (AV with arrows) */}
                        <div className="relative">
                            <button
                                disabled={!isText}
                                onClick={() => setShowCharSpacingMenu(!showCharSpacingMenu)}
                                className={cn("h-8 px-1.5 flex flex-col items-center justify-center rounded-[3px] transition-all disabled:opacity-40", showCharSpacingMenu ? "bg-slate-100" : "hover:bg-slate-100")}
                                title="Character Spacing"
                            >
                                <div className="flex items-center">
                                    <span className="text-[12px] font-bold text-slate-700">A</span>
                                    <span className="text-[12px] font-bold text-slate-700 -ml-0.5">V</span>
                                    <ChevronDown size={11} className="text-slate-400 ml-0.5" />
                                </div>
                                <div className="flex items-center gap-0">
                                    <span className="text-[7px] text-blue-500">◀</span>
                                    <div className="w-2.5 h-[1px] bg-blue-500" />
                                    <span className="text-[7px] text-blue-500">▶</span>
                                </div>
                            </button>
                            {showCharSpacingMenu && (
                                <div className="absolute top-full left-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-xl py-1 w-32 z-[100] animate-in fade-in zoom-in-95 duration-150">
                                    <button onClick={() => { onFormatText({ charSpacing: -50 }); setShowCharSpacingMenu(false); }} className="w-full px-3 py-1.5 text-left text-[11px] text-slate-700 hover:bg-slate-50 flex items-center gap-2">
                                        Very Tight
                                        {(selectedElement as any)?.charSpacing === -50 && <Check size={12} className="text-blue-600 ml-auto" />}
                                    </button>
                                    <button onClick={() => { onFormatText({ charSpacing: -25 }); setShowCharSpacingMenu(false); }} className="w-full px-3 py-1.5 text-left text-[11px] text-slate-700 hover:bg-slate-50 flex items-center gap-2">
                                        Tight
                                        {(selectedElement as any)?.charSpacing === -25 && <Check size={12} className="text-blue-600 ml-auto" />}
                                    </button>
                                    <button onClick={() => { onFormatText({ charSpacing: 0 }); setShowCharSpacingMenu(false); }} className="w-full px-3 py-1.5 text-left text-[11px] text-slate-700 hover:bg-slate-50 flex items-center gap-2">
                                        Normal
                                        {((selectedElement as any)?.charSpacing === 0 || !(selectedElement as any)?.charSpacing) && <Check size={12} className="text-blue-600 ml-auto" />}
                                    </button>
                                    <button onClick={() => { onFormatText({ charSpacing: 25 }); setShowCharSpacingMenu(false); }} className="w-full px-3 py-1.5 text-left text-[11px] text-slate-700 hover:bg-slate-50 flex items-center gap-2">
                                        Loose
                                        {(selectedElement as any)?.charSpacing === 25 && <Check size={12} className="text-blue-600 ml-auto" />}
                                    </button>
                                    <button onClick={() => { onFormatText({ charSpacing: 50 }); setShowCharSpacingMenu(false); }} className="w-full px-3 py-1.5 text-left text-[11px] text-slate-700 hover:bg-slate-50 flex items-center gap-2">
                                        Very Loose
                                        {(selectedElement as any)?.charSpacing === 50 && <Check size={12} className="text-blue-600 ml-auto" />}
                                    </button>
                                    <div className="h-[1px] bg-slate-100 my-1" />
                                    <button className="w-full px-3 py-1.5 text-left text-[11px] text-slate-700 hover:bg-slate-50 disabled:opacity-50">
                                        More Spacing...
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Bottom Row: B I U S abc AV Aa | Highlighter A-color */}
                    <div className="flex items-center gap-1.5">
                        {/* Bold */}
                        <button
                            disabled={!isText}
                            onClick={() => onFormatText({ isBold: !(selectedElement as any).isBold })}
                            className={cn("w-8 h-8 flex items-center justify-center rounded-[3px] transition-all disabled:opacity-40", (selectedElement as any)?.isBold ? "bg-blue-100" : "hover:bg-slate-100")}
                            title="Bold"
                        >
                            <span className={`text-[15px] font-bold ${(selectedElement as any)?.isBold ? 'text-slate-900' : 'text-slate-600'}`}>B</span>
                        </button>

                        {/* Italic */}
                        <button
                            disabled={!isText}
                            onClick={() => onFormatText({ isItalic: !(selectedElement as any).isItalic })}
                            className={cn("w-8 h-8 flex items-center justify-center rounded-[3px] transition-all disabled:opacity-40", (selectedElement as any)?.isItalic ? "bg-blue-100" : "hover:bg-slate-100")}
                            title="Italic"
                        >
                            <span className={`text-[15px] italic font-serif ${(selectedElement as any)?.isItalic ? 'text-slate-900' : 'text-slate-600'}`}>I</span>
                        </button>

                        {/* Underline */}
                        <button
                            disabled={!isText}
                            onClick={() => onFormatText({ isUnderline: !(selectedElement as any).isUnderline })}
                            className={cn("w-8 h-8 flex items-center justify-center rounded-[3px] transition-all disabled:opacity-40", (selectedElement as any)?.isUnderline ? "bg-blue-100" : "hover:bg-slate-100")}
                            title="Underline"
                        >
                            <span className={`text-[15px] underline underline-offset-2 ${(selectedElement as any)?.isUnderline ? 'text-slate-900' : 'text-slate-600'}`}>U</span>
                        </button>

                        {/* abc strikethrough */}
                        <button
                            disabled={!isText}
                            onClick={() => onFormatText({ isStrikethrough: !(selectedElement as any).isStrikethrough })}
                            className={cn("h-8 px-1.5 flex items-center justify-center rounded-[3px] transition-all disabled:opacity-40", (selectedElement as any)?.isStrikethrough ? "bg-blue-100" : "hover:bg-slate-100")}
                            title="Strikethrough"
                        >
                            <span className={`text-[12px] font-medium line-through ${(selectedElement as any)?.isStrikethrough ? 'text-slate-900' : 'text-slate-600'}`}>abc</span>
                            <ChevronDown size={12} className="text-slate-400 ml-0.5" />
                        </button>


                        {/* Change Case (Aa) */}
                        <div className="relative">
                            <button
                                disabled={!isText}
                                onClick={() => setShowChangeCaseMenu(!showChangeCaseMenu)}
                                className="h-8 px-1.5 flex items-center justify-center rounded-[3px] hover:bg-slate-100 transition-all disabled:opacity-40"
                                title="Change Case"
                            >
                                <span className="text-[14px] font-medium text-slate-700">Aa</span>
                                <ChevronDown size={11} className="text-slate-400 ml-0.5" />
                            </button>
                            {showChangeCaseMenu && (
                                <div className="absolute top-full left-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-xl py-1 w-44 z-[100] animate-in fade-in zoom-in-95 duration-150">
                                    <button onClick={() => applyChangeCase('sentence')} className="w-full px-3 py-1.5 text-left text-[12px] text-slate-700 hover:bg-slate-50 flex items-center gap-2">
                                        <span className="text-slate-400">Aa</span> Sentence case.
                                    </button>
                                    <button onClick={() => applyChangeCase('lowercase')} className="w-full px-3 py-1.5 text-left text-[12px] text-slate-700 hover:bg-slate-50 flex items-center gap-2">
                                        <span className="text-slate-400 underline">l</span> lowercase
                                    </button>
                                    <button onClick={() => applyChangeCase('uppercase')} className="w-full px-3 py-1.5 text-left text-[12px] text-slate-700 hover:bg-slate-50 flex items-center gap-2">
                                        <span className="text-slate-400 underline">U</span> UPPERCASE
                                    </button>
                                    <button onClick={() => applyChangeCase('capitalize')} className="w-full px-3 py-1.5 text-left text-[12px] text-slate-700 hover:bg-slate-50 flex items-center gap-2">
                                        <span className="text-slate-400 underline">C</span> Capitalize Each Word
                                    </button>
                                    <button onClick={() => applyChangeCase('toggle')} className="w-full px-3 py-1.5 text-left text-[12px] text-slate-700 hover:bg-slate-50 flex items-center gap-2">
                                        <span className="text-slate-400 underline">t</span> tOGGLE cASE
                                    </button>
                                </div>
                            )}
                        </div>

                        <div className="w-[1px] h-5 bg-slate-200 mx-1.5" />

                        {/* Clear Formatting (Ax) */}
                        <div className="relative">
                            <button
                                disabled={!isText}
                                onClick={() => onFormatText({ isBold: false, isItalic: false, isUnderline: false, highlightColor: '', color: '#000000', isStrikethrough: false })}
                                className="w-9 h-[28px] flex items-center justify-center hover:bg-slate-100 rounded-[3px] transition-colors disabled:opacity-40"
                                title="Clear All Formatting"
                            >
                                <span className="text-[16px] font-medium text-slate-600">A</span>
                                <Eraser size={Math.max(8, 11 * uiScale)} className="text-pink-500 -ml-0.5" />
                            </button>
                        </div>

                        {/* Highlighter */}
                        <div className="relative flex items-center">
                            <button
                                disabled={!isText}
                                className={cn("w-9 h-7.5 flex flex-col items-center justify-center rounded-[3px] transition-all disabled:opacity-40", (selectedElement as any)?.highlightColor ? "bg-slate-100" : "hover:bg-slate-100")}
                                title="Text Highlight Color"
                            >
                                <Highlighter size={18} strokeWidth={1.5} className={(selectedElement as any)?.highlightColor ? "text-slate-900" : "text-slate-500"} />
                                <div className="absolute top-[24px] left-1 right-3 h-0.5" style={{ backgroundColor: (selectedElement as any)?.highlightColor || 'transparent' }} />
                            </button>
                            <input
                                type="color"
                                disabled={!isText}
                                className="absolute inset-0 opacity-0 cursor-pointer w-full h-full disabled:cursor-not-allowed"
                                value={(selectedElement as any)?.highlightColor || '#ffff00'}
                                onChange={(e) => onFormatText({ highlightColor: e.target.value })}
                                title="Choose Highlight Color"
                            />
                            <ChevronDown size={11} className="text-slate-400 -ml-0.5" />
                        </div>

                        {/* Font Color */}
                        <div className="relative flex items-center">
                            <button
                                className="w-9 h-7.5 flex flex-col items-center justify-center rounded-[3px] hover:bg-slate-100 transition-all"
                                title="Font Color"
                            >
                                <span className="text-[15px] font-medium text-slate-600 leading-none">A</span>
                                <div className="w-6 h-[4px] rounded-sm mt-0.5" style={{ backgroundColor: (selectedElement as any)?.color || '#ff0000' }} />
                                <input
                                    type="color"
                                    className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
                                    value={(selectedElement as any)?.color || '#ff0000'}
                                    onChange={(e) => onFormatText({ color: e.target.value })}
                                />
                            </button>
                            <ChevronDown size={11} className="text-slate-400 -ml-0.5" />
                        </div>
                    </div>

                    {/* Footer with Label and Expand Icon */}
                    {/* <div className="mt-auto flex items-center justify-center relative pt-0.5 border-t border-slate-100">
                        <span className="text-[10px] text-slate-500 font-medium">Font</span>
                        <div className="absolute right-0 bottom-0">
                            <svg width="8" height="8" viewBox="0 0 8 8" fill="none" className="text-slate-400">
                                <path d="M1 7L7 1M7 1H3M7 1V5" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </div>
                    </div> */}
                    {uiScale >= 0.45 && <span className="text-[9px] text-center uppercase tracking-[0.2em] font-black text-slate-300 mt-auto">Font</span>}
                </div>

                {/* Paragraph Group */}
                <div className={cn("flex flex-col border-r border-slate-200 h-full py-2 relative justify-center gap-2", uiScale < 0.85 ? "px-2" : "px-4")}>
                    {/* Top Row: Lists and Indents */}
                    <div className="flex items-center gap-1.5">
                        {/* Bullet List */}
                        <div className="relative flex items-center rounded-[3px] hover:bg-slate-100 transition-all">
                            <button
                                disabled={!isText}
                                onClick={() => {
                                    if ((selectedElement as any)?.isBulleted) {
                                        onFormatText({ isBulleted: false, isNumbered: false, listType: 'none' });
                                    } else {
                                        onFormatText({ isBulleted: true, isNumbered: false, listType: 'bullet-disc' });
                                    }
                                }}
                                className={cn("w-[32px] h-8 flex items-center justify-center rounded-l-[3px] transition-all disabled:opacity-40", (selectedElement as any)?.isBulleted ? "bg-blue-100" : "hover:bg-slate-100")}
                                title="Bullets"
                            >
                                <List size={Math.max(13, 19 * uiScale)} className={(selectedElement as any)?.isBulleted ? "text-slate-900" : "text-slate-600"} />
                            </button>
                            <button
                                disabled={!isText}
                                onClick={() => setShowBulletMenu(!showBulletMenu)}
                                className={cn("w-[15px] h-8 flex items-center justify-center rounded-r-[3px] border-l border-transparent hover:border-slate-200 transition-all disabled:opacity-40", showBulletMenu ? "bg-slate-100" : "hover:bg-slate-200")}
                                title="Bullet Options"
                            >
                                <ChevronDown size={13} className="text-slate-400" />
                            </button>

                            {showBulletMenu && (
                                <div className="absolute top-full left-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-xl p-2 z-[100] animate-in fade-in zoom-in-95 duration-150 w-64 grid grid-cols-4 gap-2">
                                    <button onClick={() => { onFormatText({ isBulleted: false, listType: 'none' }); setShowBulletMenu(false); }} className="col-span-1 aspect-square flex flex-col items-center justify-center hover:bg-slate-50 border border-slate-200 rounded p-1">
                                        <span className="text-[10px] text-slate-500">None</span>
                                    </button>
                                    <button onClick={() => { onFormatText({ isBulleted: true, isNumbered: false, listType: 'bullet-disc' }); setShowBulletMenu(false); }} className="col-span-1 aspect-square flex flex-col items-center justify-center hover:bg-slate-50 border border-slate-200 rounded p-1 gap-1">
                                        <div className="flex flex-col gap-1 items-start w-full px-1">
                                            <div className="flex items-center gap-1"><div className="w-1 h-1 rounded-full bg-black"></div><div className="w-6 h-[1px] bg-slate-300"></div></div>
                                            <div className="flex items-center gap-1"><div className="w-1 h-1 rounded-full bg-black"></div><div className="w-6 h-[1px] bg-slate-300"></div></div>
                                            <div className="flex items-center gap-1"><div className="w-1 h-1 rounded-full bg-black"></div><div className="w-6 h-[1px] bg-slate-300"></div></div>
                                        </div>
                                    </button>
                                    <button onClick={() => { onFormatText({ isBulleted: true, isNumbered: false, listType: 'bullet-circle' }); setShowBulletMenu(false); }} className="col-span-1 aspect-square flex flex-col items-center justify-center hover:bg-slate-50 border border-slate-200 rounded p-1 gap-1">
                                        <div className="flex flex-col gap-1 items-start w-full px-1">
                                            <div className="flex items-center gap-1"><div className="w-1 h-1 rounded-full border border-black bg-transparent"></div><div className="w-6 h-[1px] bg-slate-300"></div></div>
                                            <div className="flex items-center gap-1"><div className="w-1 h-1 rounded-full border border-black bg-transparent"></div><div className="w-6 h-[1px] bg-slate-300"></div></div>
                                            <div className="flex items-center gap-1"><div className="w-1 h-1 rounded-full border border-black bg-transparent"></div><div className="w-6 h-[1px] bg-slate-300"></div></div>
                                        </div>
                                    </button>
                                    <button onClick={() => { onFormatText({ isBulleted: true, isNumbered: false, listType: 'bullet-square' }); setShowBulletMenu(false); }} className="col-span-1 aspect-square flex flex-col items-center justify-center hover:bg-slate-50 border border-slate-200 rounded p-1 gap-1">
                                        <div className="flex flex-col gap-1 items-start w-full px-1">
                                            <div className="flex items-center gap-1"><div className="w-1 h-1 bg-black"></div><div className="w-6 h-[1px] bg-slate-300"></div></div>
                                            <div className="flex items-center gap-1"><div className="w-1 h-1 bg-black"></div><div className="w-6 h-[1px] bg-slate-300"></div></div>
                                            <div className="flex items-center gap-1"><div className="w-1 h-1 bg-black"></div><div className="w-6 h-[1px] bg-slate-300"></div></div>
                                        </div>
                                    </button>
                                    <button onClick={() => { onFormatText({ isBulleted: true, isNumbered: false, listType: 'bullet-square-outlined' }); setShowBulletMenu(false); }} className="col-span-1 aspect-square flex flex-col items-center justify-center hover:bg-slate-50 border border-slate-200 rounded p-1 gap-1">
                                        <div className="flex flex-col gap-1 items-start w-full px-1">
                                            <div className="flex items-center gap-1"><div className="w-1 h-1 border border-black bg-transparent"></div><div className="w-6 h-[1px] bg-slate-300"></div></div>
                                            <div className="flex items-center gap-1"><div className="w-1 h-1 border border-black bg-transparent"></div><div className="w-6 h-[1px] bg-slate-300"></div></div>
                                            <div className="flex items-center gap-1"><div className="w-1 h-1 border border-black bg-transparent"></div><div className="w-6 h-[1px] bg-slate-300"></div></div>
                                        </div>
                                    </button>
                                    <button onClick={() => { onFormatText({ isBulleted: true, isNumbered: false, listType: 'bullet-diamond' }); setShowBulletMenu(false); }} className="col-span-1 aspect-square flex flex-col items-center justify-center hover:bg-slate-50 border border-slate-200 rounded p-1 gap-1">
                                        <div className="flex flex-col gap-1 items-start w-full px-1">
                                            <div className="flex items-center gap-1"><div className="w-1 h-1 bg-black rotate-45 scale-75"></div><div className="w-6 h-[1px] bg-slate-300"></div></div>
                                            <div className="flex items-center gap-1"><div className="w-1 h-1 bg-black rotate-45 scale-75"></div><div className="w-6 h-[1px] bg-slate-300"></div></div>
                                            <div className="flex items-center gap-1"><div className="w-1 h-1 bg-black rotate-45 scale-75"></div><div className="w-6 h-[1px] bg-slate-300"></div></div>
                                        </div>
                                    </button>
                                    <button onClick={() => { onFormatText({ isBulleted: true, isNumbered: false, listType: 'bullet-arrow' }); setShowBulletMenu(false); }} className="col-span-1 aspect-square flex flex-col items-center justify-center hover:bg-slate-50 border border-slate-200 rounded p-1 gap-1">
                                        <div className="flex flex-col gap-1 items-start w-full px-1">
                                            <div className="flex items-center gap-1"><span className="text-[6px] leading-none">➤</span><div className="w-6 h-[1px] bg-slate-300"></div></div>
                                            <div className="flex items-center gap-1"><span className="text-[6px] leading-none">➤</span><div className="w-6 h-[1px] bg-slate-300"></div></div>
                                            <div className="flex items-center gap-1"><span className="text-[6px] leading-none">➤</span><div className="w-6 h-[1px] bg-slate-300"></div></div>
                                        </div>
                                    </button>
                                    <button onClick={() => { onFormatText({ isBulleted: true, isNumbered: false, listType: 'bullet-check' }); setShowBulletMenu(false); }} className="col-span-1 aspect-square flex flex-col items-center justify-center hover:bg-slate-50 border border-slate-200 rounded p-1 gap-1">
                                        <div className="flex flex-col gap-1 items-start w-full px-1">
                                            <div className="flex items-center gap-1"><span className="text-[6px] leading-none">✓</span><div className="w-6 h-[1px] bg-slate-300"></div></div>
                                            <div className="flex items-center gap-1"><span className="text-[6px] leading-none">✓</span><div className="w-6 h-[1px] bg-slate-300"></div></div>
                                            <div className="flex items-center gap-1"><span className="text-[6px] leading-none">✓</span><div className="w-6 h-[1px] bg-slate-300"></div></div>
                                        </div>
                                    </button>
                                </div>
                            )}
                        </div>

                        {/* Numbered List */}
                        <div className="relative flex items-center rounded-[3px] hover:bg-slate-100 transition-all">
                            <button
                                disabled={!isText}
                                onClick={() => {
                                    if ((selectedElement as any)?.isNumbered) {
                                        onFormatText({ isBulleted: false, isNumbered: false, listType: 'none' });
                                    } else {
                                        onFormatText({ isBulleted: false, isNumbered: true, listType: 'number-decimal' });
                                    }
                                }}
                                className={cn("w-[32px] h-8 flex items-center justify-center rounded-l-[3px] transition-all disabled:opacity-40", (selectedElement as any)?.isNumbered ? "bg-blue-100" : "hover:bg-slate-100")}
                                title="Numbering"
                            >
                                <ListOrdered size={Math.max(13, 19 * uiScale)} className={(selectedElement as any)?.isNumbered ? "text-slate-900" : "text-slate-600"} />
                            </button>
                            <button
                                disabled={!isText}
                                onClick={() => setShowNumberingMenu(!showNumberingMenu)}
                                className={cn("w-[15px] h-8 flex items-center justify-center rounded-r-[3px] border-l border-transparent hover:border-slate-200 transition-all disabled:opacity-40", showNumberingMenu ? "bg-slate-100" : "hover:bg-slate-200")}
                                title="Numbering Options"
                            >
                                <ChevronDown size={13} className="text-slate-400" />
                            </button>

                            {showNumberingMenu && (
                                <div className="absolute top-full left-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-xl p-2 z-[100] animate-in fade-in zoom-in-95 duration-150 w-64 grid grid-cols-4 gap-2">
                                    <button onClick={() => { onFormatText({ isNumbered: false, listType: 'none' }); setShowNumberingMenu(false); }} className="col-span-1 aspect-square flex flex-col items-center justify-center hover:bg-slate-50 border border-slate-200 rounded p-1">
                                        <span className="text-[10px] text-slate-500">None</span>
                                    </button>
                                    <button onClick={() => { onFormatText({ isNumbered: true, isBulleted: false, listType: 'number-decimal' }); setShowNumberingMenu(false); }} className="col-span-1 aspect-square flex flex-col items-center justify-center hover:bg-slate-50 border border-slate-200 rounded p-1 gap-1">
                                        <div className="flex flex-col gap-1 items-start w-full px-1">
                                            <div className="flex items-center gap-1"><span className="text-[6px] font-bold">1.</span><div className="w-6 h-[1px] bg-slate-300"></div></div>
                                            <div className="flex items-center gap-1"><span className="text-[6px] font-bold">2.</span><div className="w-6 h-[1px] bg-slate-300"></div></div>
                                            <div className="flex items-center gap-1"><span className="text-[6px] font-bold">3.</span><div className="w-6 h-[1px] bg-slate-300"></div></div>
                                        </div>
                                    </button>
                                    <button onClick={() => { onFormatText({ isNumbered: true, isBulleted: false, listType: 'number-decimal-paren' }); setShowNumberingMenu(false); }} className="col-span-1 aspect-square flex flex-col items-center justify-center hover:bg-slate-50 border border-slate-200 rounded p-1 gap-1">
                                        <div className="flex flex-col gap-1 items-start w-full px-1">
                                            <div className="flex items-center gap-1"><span className="text-[6px] font-bold">1)</span><div className="w-6 h-[1px] bg-slate-300"></div></div>
                                            <div className="flex items-center gap-1"><span className="text-[6px] font-bold">2)</span><div className="w-6 h-[1px] bg-slate-300"></div></div>
                                            <div className="flex items-center gap-1"><span className="text-[6px] font-bold">3)</span><div className="w-6 h-[1px] bg-slate-300"></div></div>
                                        </div>
                                    </button>
                                    <button onClick={() => { onFormatText({ isNumbered: true, isBulleted: false, listType: 'number-roman-upper' }); setShowNumberingMenu(false); }} className="col-span-1 aspect-square flex flex-col items-center justify-center hover:bg-slate-50 border border-slate-200 rounded p-1 gap-1">
                                        <div className="flex flex-col gap-1 items-start w-full px-1">
                                            <div className="flex items-center gap-1"><span className="text-[6px] font-bold">I.</span><div className="w-6 h-[1px] bg-slate-300"></div></div>
                                            <div className="flex items-center gap-1"><span className="text-[6px] font-bold">II.</span><div className="w-6 h-[1px] bg-slate-300"></div></div>
                                            <div className="flex items-center gap-1"><span className="text-[6px] font-bold">III.</span><div className="w-6 h-[1px] bg-slate-300"></div></div>
                                        </div>
                                    </button>
                                    <button onClick={() => { onFormatText({ isNumbered: true, isBulleted: false, listType: 'number-alpha-upper' }); setShowNumberingMenu(false); }} className="col-span-1 aspect-square flex flex-col items-center justify-center hover:bg-slate-50 border border-slate-200 rounded p-1 gap-1">
                                        <div className="flex flex-col gap-1 items-start w-full px-1">
                                            <div className="flex items-center gap-1"><span className="text-[6px] font-bold">A.</span><div className="w-6 h-[1px] bg-slate-300"></div></div>
                                            <div className="flex items-center gap-1"><span className="text-[6px] font-bold">B.</span><div className="w-6 h-[1px] bg-slate-300"></div></div>
                                            <div className="flex items-center gap-1"><span className="text-[6px] font-bold">C.</span><div className="w-6 h-[1px] bg-slate-300"></div></div>
                                        </div>
                                    </button>
                                    <button onClick={() => { onFormatText({ isNumbered: true, isBulleted: false, listType: 'number-alpha-lower-paren' }); setShowNumberingMenu(false); }} className="col-span-1 aspect-square flex flex-col items-center justify-center hover:bg-slate-50 border border-slate-200 rounded p-1 gap-1">
                                        <div className="flex flex-col gap-1 items-start w-full px-1">
                                            <div className="flex items-center gap-1"><span className="text-[6px] font-bold">a)</span><div className="w-6 h-[1px] bg-slate-300"></div></div>
                                            <div className="flex items-center gap-1"><span className="text-[6px] font-bold">b)</span><div className="w-6 h-[1px] bg-slate-300"></div></div>
                                            <div className="flex items-center gap-1"><span className="text-[6px] font-bold">c)</span><div className="w-6 h-[1px] bg-slate-300"></div></div>
                                        </div>
                                    </button>
                                    <button onClick={() => { onFormatText({ isNumbered: true, isBulleted: false, listType: 'number-alpha-lower' }); setShowNumberingMenu(false); }} className="col-span-1 aspect-square flex flex-col items-center justify-center hover:bg-slate-50 border border-slate-200 rounded p-1 gap-1">
                                        <div className="flex flex-col gap-1 items-start w-full px-1">
                                            <div className="flex items-center gap-1"><span className="text-[6px] font-bold">a.</span><div className="w-6 h-[1px] bg-slate-300"></div></div>
                                            <div className="flex items-center gap-1"><span className="text-[6px] font-bold">b.</span><div className="w-6 h-[1px] bg-slate-300"></div></div>
                                            <div className="flex items-center gap-1"><span className="text-[6px] font-bold">c.</span><div className="w-6 h-[1px] bg-slate-300"></div></div>
                                        </div>
                                    </button>
                                    <button onClick={() => { onFormatText({ isNumbered: true, isBulleted: false, listType: 'number-roman-lower' }); setShowNumberingMenu(false); }} className="col-span-1 aspect-square flex flex-col items-center justify-center hover:bg-slate-50 border border-slate-200 rounded p-1 gap-1">
                                        <div className="flex flex-col gap-1 items-start w-full px-1">
                                            <div className="flex items-center gap-1"><span className="text-[6px] font-bold">i.</span><div className="w-6 h-[1px] bg-slate-300"></div></div>
                                            <div className="flex items-center gap-1"><span className="text-[6px] font-bold">ii.</span><div className="w-6 h-[1px] bg-slate-300"></div></div>
                                            <div className="flex items-center gap-1"><span className="text-[6px] font-bold">iii.</span><div className="w-6 h-[1px] bg-slate-300"></div></div>
                                        </div>
                                    </button>
                                </div>
                            )}
                        </div>

                        <div className="w-[1px] h-6 bg-slate-200 mx-1.5" />

                        {/* Decrease Indent */}
                        <button
                            disabled={!isText}
                            className="w-7 h-7 flex items-center justify-center rounded-[3px] hover:bg-slate-100 transition-all disabled:opacity-40"
                            title="Decrease List Level"
                        >
                            <svg width="18" height="18" viewBox="0 0 14 14" fill="none" className="text-slate-600">
                                <path d="M2 3H12M6 7H12M6 11H12M2 7L4 9L2 11" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </button>

                        {/* Increase Indent */}
                        <button
                            disabled={!isText}
                            className="w-8 h-8 flex items-center justify-center rounded-[3px] hover:bg-slate-100 transition-all disabled:opacity-40"
                            title="Increase List Level"
                        >
                            <svg width="18" height="18" viewBox="0 0 14 14" fill="none" className="text-slate-600">
                                <path d="M2 3H12M6 7H12M6 11H12M4 7L2 9L4 11" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </button>
                    </div>

                    {/* Bottom Row: Alignment */}
                    <div className="flex items-center gap-0.5">
                        <button
                            disabled={!isText}
                            onClick={() => onFormatText({ textAlign: 'left' })}
                            className={cn("w-8 h-8 flex items-center justify-center rounded-[3px] transition-all disabled:opacity-40", (selectedElement as any)?.textAlign === 'left' ? "bg-blue-100" : "hover:bg-slate-100")}
                            title="Align Left"
                        >
                            <AlignLeft size={Math.max(13, 19 * uiScale)} className={(selectedElement as any)?.textAlign === 'left' ? "text-slate-900" : "text-slate-600"} />
                        </button>
                        <button
                            disabled={!isText}
                            onClick={() => onFormatText({ textAlign: 'center' })}
                            className={cn("w-8 h-8 flex items-center justify-center rounded-[3px] transition-all disabled:opacity-40", (selectedElement as any)?.textAlign === 'center' ? "bg-blue-100" : "hover:bg-slate-100")}
                            title="Center"
                        >
                            <AlignCenter size={Math.max(13, 19 * uiScale)} className={(selectedElement as any)?.textAlign === 'center' ? "text-slate-900" : "text-slate-600"} />
                        </button>
                        <button
                            disabled={!isText}
                            onClick={() => onFormatText({ textAlign: 'right' })}
                            className={cn("w-8 h-8 flex items-center justify-center rounded-[3px] transition-all disabled:opacity-40", (selectedElement as any)?.textAlign === 'right' ? "bg-blue-100" : "hover:bg-slate-100")}
                            title="Align Right"
                        >
                            <AlignRight size={Math.max(13, 19 * uiScale)} className={(selectedElement as any)?.textAlign === 'right' ? "text-slate-900" : "text-slate-600"} />
                        </button>
                        <button
                            disabled={!isText}
                            onClick={() => onFormatText({ textAlign: 'justify' })}
                            className={cn("w-8 h-8 flex items-center justify-center rounded-[3px] transition-all disabled:opacity-40", (selectedElement as any)?.textAlign === 'justify' ? "bg-blue-100" : "hover:bg-slate-100")}
                            title="Justify"
                        >
                            <AlignJustify size={Math.max(13, 19 * uiScale)} className={(selectedElement as any)?.textAlign === 'justify' ? "text-slate-900" : "text-slate-600"} />
                        </button>
                    </div>

                    {uiScale >= 0.45 && <span className="text-[9px] text-center uppercase tracking-[0.2em] font-black text-slate-300 mt-auto">Paragraph</span>}
                    {/* Footer with Label and Expand Icon */}
                    {/* <div className="mt-auto flex items-center justify-center relative pt-0.5 border-t border-slate-100">
                        <span className="text-[10px] text-slate-500 font-medium">Paragraph</span>
                        <div className="absolute right-0 bottom-0">
                            <svg width="8" height="8" viewBox="0 0 8 8" fill="none" className="text-slate-400">
                                <path d="M1 7L7 1M7 1H3M7 1V5" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </div>

                    </div> */}
                </div>

                {/* Drawing Group */}
                <div className="flex flex-col items-center border-r border-slate-100 px-2 gap-1 h-full py-1.5 shrink-0">
                    <div className="flex items-center justify-center gap-1 my-auto">
                        <div className="relative group/shapes flex justify-center">
                            <button
                                className="flex flex-col items-center justify-center gap-1.5 px-3 py-1.5 hover:bg-slate-50 rounded-lg group transition-all min-w-[50px]"
                                onClick={() => (window as any)._toggleShapesMenu?.()}
                            >
                                <div className="bg-blue-50 p-1.5 rounded-lg group-hover:scale-105 transition-transform">
                                    <Square size={Math.max(14, 20 * uiScale)} className="text-blue-600" />
                                </div>
                                <span className="text-[10px] font-bold text-slate-700">Shapes</span>
                            </button>
                            <ShapesDropdown onAddShape={(s) => { onAddShape(s); }} uiScale={uiScale} />
                        </div>

                        <div className="w-[1px] h-10 bg-slate-100 mx-1" />

                        <button onClick={onAddText} className="flex flex-col items-center justify-center gap-1.5 px-3 py-1.5 hover:bg-slate-50 rounded-lg text-slate-600 transition-colors min-w-[50px]" title="Text Box">
                            <div className="p-1.5">
                                <Type size={Math.max(14, 20 * uiScale)} />
                            </div>
                            <span className="text-[10px] font-medium text-slate-500">Text Box</span>
                        </button>

                        <div className="w-[1px] h-10 bg-slate-100 mx-1" />

                        <button onClick={() => fileInputRef.current?.click()} className="flex flex-col items-center justify-center gap-1.5 px-3 py-1.5 hover:bg-slate-50 rounded-lg text-slate-600 transition-colors min-w-[50px]" title="Image">
                            <div className="p-1.5">
                                <ImageIcon size={Math.max(14, 20 * uiScale)} />
                            </div>
                            <span className="text-[10px] font-medium text-slate-500">Image</span>
                            <input type="file" ref={fileInputRef} className="hidden" accept="image/*" onChange={(e) => e.target.files?.[0] && onAddImage(e.target.files[0])} />
                        </button>
                    </div>
                    {uiScale >= 0.45 && <span className="text-[9px] uppercase tracking-[0.2em] font-black text-slate-300 mt-auto">Drawing</span>}
                </div>

                {/* AI Text Group */}
                <div className="flex flex-col items-center border-r border-slate-100 px-1 gap-1 h-full py-1.5 relative">
                    <div className="flex items-center justify-center gap-0.5 my-auto h-full relative">
                        <button
                            disabled={!isText || isAiLoading}
                            onClick={() => setShowAiMenu(!showAiMenu)}
                            className={cn(
                                "flex flex-col items-center justify-center gap-1.5 px-3 py-1.5 hover:bg-slate-50 rounded-lg text-slate-600 disabled:opacity-20 transition-all min-w-[72px] outline-none focus:outline-none focus:ring-0 border-none",
                                showAiMenu && "bg-slate-100 text-slate-900"
                            )}
                        >
                            <div className="p-1.5 rounded-lg group-hover:scale-110 transition-transform">
                                <Sparkles size={Math.max(14, 20 * uiScale)} className="text-blue-500" />
                            </div>
                            <div className="flex items-center gap-1">
                                <span className="text-[10px] font-bold">Refine</span>
                                <ChevronDown size={10} className="text-slate-400" />
                            </div>
                        </button>

                        {showAiMenu && (
                            <div className="absolute top-full right-0 mt-2 bg-white border border-slate-200 rounded-xl shadow-xl w-48 z-[100] animate-in fade-in zoom-in-95 duration-150 overflow-hidden">
                                <button
                                    onClick={() => { onRefineAction?.(selectedElement!.id, 'shorten'); setShowAiMenu(false); }}
                                    // className="w-full px-5 py-4 text-left text-xs font-medium text-slate-700 hover:bg-purple-50 hover:text-purple-700 flex items-center gap-3 transition-colors border-b border-slate-50"
                                    className="w-full flex items-center gap-3 px-4 py-2 hover:bg-slate-50 text-[13px] font-bold text-slate-700 transition-all "
                                >
                                    <div className="p-2 text-blue-600 group-hover:bg-blue-600 group-hover:text-white transition-colors">
                                        <ArrowDownWideNarrow size={14} className="text-blue-500" />
                                    </div>
                                    {/* <span className="text-purple-400">abc</span> */}
                                    Shorten
                                </button>
                                <button
                                    onClick={() => { onRefineAction?.(selectedElement!.id, 'reframe'); setShowAiMenu(false); }}
                                    // className="w-full px-5 py-4 text-left text-xs font-medium text-slate-700 hover:bg-blue-50 hover:text-blue-700 flex items-center gap-3 transition-colors border-b border-slate-50"
                                    className="w-full flex items-center gap-3 px-4 py-2 hover:bg-slate-50 text-[13px] font-bold text-slate-700 transition-all "
                                >
                                    {/* <span className="text-blue-400">↻</span> */}
                                    <div className="p-2 text-blue-600 group-hover:bg-blue-600 group-hover:text-white transition-colors">
                                        <RefreshCcwDot size={14} className="text-blue-500" />
                                    </div>
                                    Reframe
                                </button>
                                <button
                                    onClick={() => { onRefineAction?.(selectedElement!.id, 'lengthen'); setShowAiMenu(false); }}
                                    // className="w-full px-5 py-4 text-left text-xs font-medium text-slate-700 hover:bg-emerald-50 hover:text-emerald-700 flex items-center gap-3 transition-colors"
                                    className="w-full flex items-center gap-3 px-4 py-2 hover:bg-slate-50 text-[13px] font-bold text-slate-700 transition-all "
                                >
                                    {/* <span className="text-emerald-400">...</span> */}
                                    <div className="p-2 text-blue-600 group-hover:bg-blue-600 group-hover:text-white transition-colors">
                                        <WrapText size={14} className="text-blue-500" />
                                    </div>
                                    Lengthen
                                </button>
                            </div>
                        )}
                    </div>

                    {/* AI Loading State */}
                    {isAiLoading && (
                        <div className="absolute inset-x-0 top-2 bottom-8 bg-white/80 flex items-center justify-center rounded-lg z-10 backdrop-blur-[1px]">
                            <RefreshCw size={16} className="text-blue-600 animate-spin" />
                        </div>
                    )}
                    {uiScale >= 0.45 && <span className="text-[9px] uppercase tracking-[0.2em] font-black text-slate-300 mt-auto">AI Text</span>}
                </div>

                {/* Actions Group */}
                <div className="flex flex-col items-center pl-3 pr-3 gap-1 h-full py-2 relative">
                    <div className="flex items-center justify-center gap-1 my-auto h-full">
                        <button
                            onClick={onPlay}
                            className="flex flex-col items-center justify-center gap-1.5 px-3 py-1.5 hover:bg-slate-50 rounded-lg text-slate-700 transition-all min-w-[40px] group"
                            title="Present"
                        >
                            <div className="p-1.5 rounded-lg group-hover:scale-110 transition-transform">
                                <Play size={Math.max(14, 20 * uiScale)} className="fill-slate-700 group-hover:fill-slate-900" />
                            </div>
                            <span className="text-[10px] font-bold">Present</span>
                        </button>

                        <div className="w-[1px] h-10 bg-slate-100 mx-1" />

                        <button
                            onClick={onExport}
                            className="flex flex-col items-center justify-center gap-1.5 px-3 py-1.5 hover:bg-slate-50 rounded-lg text-slate-700 transition-all min-w-[50px] group"
                            title="Export"
                        >
                            <div className="p-1.5 rounded-lg group-hover:scale-110 transition-transform">
                                <Download size={Math.max(14, 20 * uiScale)} className="text-emerald-600" />
                            </div>
                            <span className="text-[10px] font-bold">Export</span>
                        </button>

                        {selectedElement && (
                            <>
                                <div className="w-[1px] h-10 bg-slate-100 mx-1" />
                                <button
                                    onClick={onDeleteElement}
                                    className="flex flex-col items-center justify-center gap-1.5 px-3 py-1.5 hover:bg-red-50 rounded-lg text-red-600 transition-all min-w-[50px] group"
                                    title="Delete"
                                >
                                    <div className="p-1.5 rounded-lg group-hover:scale-110 transition-transform">
                                        <Trash2 size={Math.max(14, 20 * uiScale)} />
                                    </div>
                                    <span className="text-[10px] font-bold">Delete</span>
                                </button>
                            </>
                        )}
                    </div>

                    <div className="absolute top-2 right-3 flex items-center gap-1 px-1.5 py-0.5 bg-emerald-50/50 rounded-md border border-emerald-100/50">
                        <Check size={9} className="text-emerald-500" />
                        <span className="text-[7px] font-black text-emerald-600/70 tracking-tighter uppercase">Saved</span>
                    </div>

                    {uiScale >= 0.45 && <span className="text-[9px] uppercase tracking-[0.2em] font-black text-slate-300 mt-auto">Actions</span>}
                </div>
            </div>

            {/* Upload Modal */}
            {showUploadModal && (
                <>
                    <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-[2000]" onClick={() => setShowUploadModal(false)} />
                    <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[480px] bg-white rounded-3xl shadow-2xl z-[2001] border border-slate-200 p-8 animate-in fade-in zoom-in-95 duration-200">
                        <div className="flex justify-between items-center mb-6">
                            <h3 className="text-lg font-black text-slate-800 tracking-tight flex items-center gap-3">
                                <div className="p-2 bg-blue-50 text-blue-600 rounded-xl">
                                    <Download size={20} />
                                </div>
                                Upload Presentation
                            </h3>
                            <button onClick={() => setShowUploadModal(false)} className="text-slate-400 hover:text-slate-600 transition-colors">
                                <RefreshCw size={20} className="rotate-45" />
                            </button>
                        </div>

                        <div className="flex gap-2 p-1 bg-slate-50 rounded-2xl mb-8">
                            <button
                                onClick={() => setUploadType('file')}
                                className={cn(
                                    "flex-1 py-3 text-xs font-black rounded-xl transition-all",
                                    uploadType === 'file' ? "bg-white text-blue-600 shadow-sm" : "text-slate-500 hover:text-slate-700"
                                )}
                            >
                                FILE UPLOAD
                            </button>
                            <button
                                onClick={() => setUploadType('link')}
                                className={cn(
                                    "flex-1 py-3 text-xs font-black rounded-xl transition-all",
                                    uploadType === 'link' ? "bg-white text-blue-600 shadow-sm" : "text-slate-500 hover:text-slate-700"
                                )}
                            >
                                LINK UPLOAD
                            </button>
                        </div>

                        {uploadType === 'file' ? (
                            <label className="flex flex-col items-center justify-center gap-4 py-12 border-2 border-dashed border-slate-200 rounded-3xl hover:border-blue-400 hover:bg-blue-50/50 transition-all cursor-pointer group">
                                <div className="p-4 bg-white rounded-2xl shadow-sm border border-slate-100 group-hover:scale-110 transition-transform">
                                    <Plus size={24} className="text-blue-500" />
                                </div>
                                <div className="text-center">
                                    <p className="text-sm font-bold text-slate-700">Choose a PPTX file</p>
                                    <p className="text-xs text-slate-400 mt-1">Maximum file size: 50MB</p>
                                </div>
                                <input
                                    type="file"
                                    accept=".pptx"
                                    className="hidden"
                                    onChange={(e) => {
                                        const file = e.target.files?.[0];
                                        if (file) {
                                            onLoadPresentation(file);
                                            setShowUploadModal(false);
                                        }
                                    }}
                                />
                            </label>
                        ) : (
                            <div className="space-y-4">
                                <div className="relative">
                                    <input
                                        type="text"
                                        placeholder="Paste S3 or public URL"
                                        value={uploadUrl}
                                        onChange={(e) => setUploadUrl(e.target.value)}
                                        className="w-full bg-slate-50 border border-slate-200 rounded-2xl px-5 py-4 focus:outline-none focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 text-sm placeholder:text-slate-400 text-black"
                                    />
                                </div>
                                <button
                                    onClick={() => {
                                        if (uploadUrl.trim()) {
                                            onLoadPresentation(uploadUrl.trim());
                                            setShowUploadModal(false);
                                        }
                                    }}
                                    disabled={!uploadUrl.trim()}
                                    className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 text-white font-black py-4 rounded-2xl transition-all shadow-lg shadow-blue-500/20"
                                >
                                    LOAD PRESENTATION
                                </button>
                                <p className="text-[10px] text-slate-400 text-center uppercase tracking-widest font-black">
                                    Supports S3, Dropbox, and public URLS
                                </p>
                            </div>
                        )}
                    </div>
                </>
            )}
        </div>
    );
};
